import React, { FC, useState, ReactNode } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay } from '@mui/x-date-pickers';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import moment, { Moment } from 'moment';
import { PickersDayProps } from '@mui/x-date-pickers';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/system';
import { ClickAwayListener, IconButton, IconButtonProps } from '@mui/material';

export const ClearIcon = styled(IconButton)<IconButtonProps>(({ theme }) => ({
    color: '#0000008a',
    fontSize: '1.5rem',
    border: '0',
    margin: 0,

    padding: '4px',
    marginRight: '-4px',
    display: 'none',

    '& > svg': {
        width: '1em',
        height: '1em',
        fontSize: '1.25rem',
    },
}));

export const StyledTextField = styled(TextField)<TextFieldProps>(({ theme }) => ({
    minWidth: '170px',

    '&:hover .number-filter-clear': {
        display: 'inline-flex',
        visibility: 'visible',
    },

    '& .MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputAdornedEnd': {
        paddingRight: '0px',
        fontSize: '13px',
    },

    '& .MuiInputAdornment-root.MuiInputAdornment-positionEnd.MuiInputAdornment-outlined.MuiInputAdornment-sizeMedium': {
        marginLeft: '2px',
    },
}));

export type DateRangeValueType = Moment | null;
export type DateRangeValuesType = [DateRangeValueType, DateRangeValueType];

export type PropValueType = string[] | undefined;
export interface DateRangePropsType {
    value?: PropValueType;
    onChange?: (value: PropValueType) => void;
    label?: string;
    disabled?: boolean;
    required?: boolean;
    format?: string;
    fullWidth?: boolean;
}

const formatPropValues = (values?: PropValueType): DateRangeValuesType => {
    if (values && values[0] && values[1]) {
        return [moment(values[0]), moment(values[1])];
    }
    return [null, null];
};

const DateRange: FC<DateRangePropsType> = (props) => {
    const [values, setValues] = useState<DateRangeValuesType>([null, null]);
    const [open, setOpen] = useState<boolean>(false);
    const [selectFirst, setSelectFirst] = useState<boolean>(true);
    const [inputValue, setInputValue] = useState<string>('');

    const formatDate = props.format || moment.localeData().longDateFormat('L').toUpperCase().replace('YYYY', 'YY');

    const formatedPropValues = formatPropValues(props.value);

    const curentValues = open && values[0] ? values : formatedPropValues;

    const handleOpen = () => {
        setOpen(!open);
        setSelectFirst(true);
        setValues([null, null]);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectFirst(true);
        setValues([null, null]);
    };

    const handleChangeValues = (newValues: [Moment, Moment] | undefined) => {
        if (props.onChange) {
            if (!newValues) {
                if (props.value !== undefined) {
                    props.onChange(undefined);
                }
            } else if (newValues[0] && newValues[1]) {
                const sortedValues: [Moment, Moment] = newValues.sort(
                    (a: DateRangeValueType, b: DateRangeValueType): number => (a?.valueOf() || 0) - (b?.valueOf() || 0)
                );
                sortedValues[0]?.startOf('day');
                sortedValues[1].endOf('day');
                props.onChange([newValues[0].toISOString(), newValues[1].toISOString()]);
                setSelectFirst(true);
                setValues(newValues);
            }
        }
    };

    const handlePickDate = (newValue: Moment | null) => {
        if (!newValue) {
            return;
        }

        if (selectFirst) {
            setValues([newValue, null]);
            setSelectFirst(!selectFirst);
        } else {
            if (values[0]) {
                handleChangeValues([values[0], newValue]);
            }
            // handleClose();
        }
    };

    const handleFocusInput = (initText: string) => {
        setInputValue(initText);
    };

    const handleBlurInput = () => {
        setInputValue('');
    };

    const handleChangeInput = (newStr: string) => {
        const items = newStr.replace(/\s/g, '').split('-');

        if (newStr === '') {
            handleChangeValues(undefined);
        } else if (
            values.length === 2 &&
            items[0] === moment(items[0], formatDate).format(formatDate) &&
            items[1] === moment(items[1], formatDate).format(formatDate)
        ) {
            handleChangeValues([moment(items[0], formatDate), moment(items[1], formatDate)]);
        }
        setInputValue(newStr);
    };

    const getCurrentValuesText = (): string => {
        let str = curentValues[0] && curentValues[0].format(formatDate);
        if (curentValues[1]) {
            if (props.format) {
                str += ` - ${curentValues[1].format(formatDate)}`;
            } else {
                str += `-${curentValues[1].format(formatDate)}`;
            }
        }
        return str || '';
    };

    const handleClear = () => {
        handleChangeValues(undefined);
    };

    const renderInput = (params: TextFieldProps) => {
        const placeholder = `${formatDate}-${formatDate}`;
        const inputText = inputValue || getCurrentValuesText() || '';

        if (!props.disabled && !props.required && params.InputProps?.endAdornment) {
            params.InputProps.endAdornment = (
                <>
                    {!!inputText && (
                        <ClearIcon className="number-filter-clear" onClick={() => handleClear()} size="medium">
                            <CloseIcon />
                        </ClearIcon>
                    )}
                    {params.InputProps?.endAdornment}
                </>
            );
        }

        return (
            <StyledTextField
                {...params}
                inputProps={{
                    value: inputText,
                    placeholder,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChangeInput(e.target.value),
                    onFocus: () => handleFocusInput(inputText || ''),
                    onBlur: () => handleBlurInput(),
                }}
                fullWidth={props.fullWidth}
                autoComplete="off"
                className={`${inputText ? 'has-value' : ''}`}
            />
        );
    };

    const renderDay = (day: Moment, selectedDays: Moment[], pickersDayProps: PickersDayProps<Moment>): JSX.Element => {
        let className = '';
        if (curentValues[0] && curentValues[1]) {
            if (day.isSameOrAfter(curentValues[0], 'day') && day.isSameOrBefore(curentValues[1], 'day')) {
                className = 'MuiPickersRange-selected';
                if (day.isSame(curentValues[0], 'day')) {
                    className += ' MuiPickersRange-selected-start';
                }
                if (day.isSame(curentValues[1], 'day')) {
                    className += ' MuiPickersRange-selected-end';
                }
            }
        } else {
            if (curentValues[0]) {
                if (day.isSameOrAfter(curentValues[0], 'day') && day.isSameOrBefore(curentValues[0], 'day')) {
                    className = 'MuiPickersRange-selected MuiPickersRange-selected-start MuiPickersRange-selected-end';
                }
            }
        }
        return <PickersDay {...pickersDayProps} className={className} />;
    };

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        if (!open) {
            return;
        }
        // @ts-ignore
        let a = event.target.parentNode;
        while (a) {
            // @ts-ignore
            if (((typeof a.className == 'string' && a.className) || '').indexOf('MuiCalendarOrClockPicker-root') > -1) {
                return;
            }
            // @ts-ignore
            a = a.parentNode;
        }
        handleClose();
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <DatePicker
                label={props.label || ''}
                openTo="day"
                views={['year', 'month', 'day']}
                value={null}
                onChange={handlePickDate}
                renderInput={renderInput}
                closeOnSelect={false}
                open={open}
                onOpen={handleOpen}
                renderDay={renderDay}
                showDaysOutsideCurrentMonth
                disabled={props.disabled}
                componentsProps={{
                    actionBar: {
                        actions: ['cancel', 'accept'],
                    },
                }}
            />
        </ClickAwayListener>
    );
};

export default DateRange;
